import { createReducer, createAction, createSelector } from "@reduxjs/toolkit";
import { call, put, takeLatest } from "redux-saga/effects";

import { fetchSampleParameters } from "../lib/api";

export const actions = {
  request_sample_search_parameters: createAction("REQUEST_SAMPLE_SEARCH_PARAMETERS"),
  receive_sample_search_parameters: createAction("RECEIVE_SAMPLE_SEARCH_PARAMETERS"),
  receive_sample_search_parameters_failed: createAction("RECEIVE_SAMPLE_SEARCH_PARAMETERS_FAILED"),
  toggle_component: createAction("TOGGLE_COMPONENT"),
  toggle_component_type: createAction("TOGGLE_COMPONENT_TYPE"),
  update_range: createAction("UPDATE_RANGE"),
  toggle_range_filter: createAction("TOGGLE_RANGE_FILTER"),
};

export const constants = {
  //loading states
  LOADED: "LOADED",
  LOADING: "LOADING",
  ERROR: "ERROR",
  //component fields
  POLYMERS: "polymers",
  SALTS: "salts",
  SOLVENTS: "solvents",
  MONOMERS: "monomers",
  //numeric range fields
  SALT_WEIGHT_FRACTION: "salt_weight_fraction",
  CONDUCTIVITY_S_OVER_CM_50C: "conductivity_s_over_cm_50c",
  MAX_STIR_RATE_RPM: "max_stir_rate_rpm",
  MAX_HEATING_TEMP: "max_heating_temp",
  //date range fields
  SAMPLE_TIME: "sample_time",
};

export const numericRangeFields = [
  constants.SALT_WEIGHT_FRACTION,
  constants.CONDUCTIVITY_S_OVER_CM_50C,
  constants.MAX_STIR_RATE_RPM,
  constants.MAX_HEATING_TEMP,
];

export const dateRangeFields = [constants.SAMPLE_TIME];

export const components = [constants.POLYMERS, constants.SALTS, constants.SOLVENTS, constants.MONOMERS];

export const initState = {
  loadingState: constants.LOADING,
  [constants.SALTS]: {
    checked: {},
    available: [],
  },
  [constants.SOLVENTS]: {
    checked: {},
    available: [],
  },
  [constants.POLYMERS]: {
    checked: {},
    available: [],
  },
  [constants.MONOMERS]: {
    checked: {},
    available: [],
  },
  [constants.SAMPLE_TIME]: {
    checked: false,
    filterRange: [new Date().toISOString(), new Date().toISOString()],
  },
  [constants.SALT_WEIGHT_FRACTION]: {
    checked: true,
    range: [0, 10],
    filterRange: [1, 9],
  },
  [constants.CONDUCTIVITY_S_OVER_CM_50C]: {
    checked: true,
    range: [0, 10],
    filterRange: [1, 9],
  },
  [constants.MAX_STIR_RATE_RPM]: {
    checked: true,
    range: [0, 10],
    filterRange: [1, 9],
  },
  [constants.MAX_HEATING_TEMP]: {
    checked: true,
    range: [0, 10],
    filterRange: [1, 9],
  },
};

export const actionMap = {
  [actions.request_sample_search_parameters]: (state) => {
    state.loadingState = constants.LOADING;
    return state;
  },
  [actions.receive_sample_search_parameters]: (state, action) => {
    const { query_params } = action.payload;
    state.loadingState = constants.LOADED;

    components.forEach((component) => {
      state[component].available = query_params[component];
      state[component].checked = query_params[component].reduce((acc, chemical) => {
        //default all chemicals as available in search
        acc[chemical] = true;
        return acc;
      }, {});
    });

    numericRangeFields.forEach((field) => {
      state[field].range = query_params[field];
      state[field].filterRange = query_params[field];
    });

    state.loadingState = constants.LOADED;

    return state;
  },
  [actions.receive_sample_search_parameters_failed]: (state) => {
    return { ...state, loadingState: constants.ERROR };
  },
  [actions.toggle_component]: (state, action) => {
    const { component_type, component } = action.payload;
    state[component_type].checked[component] = !state[component_type].checked[component];
    return state;
  },
  [actions.toggle_component_type]: (state, action) => {
    const { component_type } = action.payload;

    const checked = Object.entries(state[component_type].checked)
      .filter(([_, isChecked]) => isChecked)
      .map(([component]) => component);
    const available = state[component_type].available.slice();
    const allChecked = checked.length === available.length;

    if (allChecked) {
      for (let component of available) {
        state[component_type].checked[component] = false;
      }
    } else {
      for (let component of available) {
        state[component_type].checked[component] = true;
      }
    }

    return state;
  },
  [actions.update_range]: (state, action) => {
    const { filterRange, field } = action.payload;
    state[field].filterRange = filterRange;
    return state;
  },
  [actions.toggle_range_filter]: (state, action) => {
    const { field } = action.payload;
    state[field].checked = !state[field].checked;
    return state;
  },
};

export const reducer = createReducer(initState, actionMap);

const getExperimentSearch = (state) => state.experimentSearch;
const getExperimentSearchWtihParsedDates = createSelector([getExperimentSearch], (searchState) => {
  return {
    ...searchState,
    ...dateRangeFields.reduce((acc, field) => {
      acc[field] = {
        ...searchState[field],
        filterRange: searchState[field].filterRange.map((ds) => new Date(ds)),
      };
      return acc;
    }, {}),
  };
});

export const selectors = {
  getExperimentSearch,
  getExperimentSearchWtihParsedDates,
};

function* onFetchSampleSearchParameters(action) {
  try {
    const { accessToken } = action.payload;
    const { data: query_params } = yield call(fetchSampleParameters, {
      accessToken,
    });

    for (let param in query_params) {
      if (Array.isArray(query_params[param]) && query_params[param].every((e) => e === null)) {
        query_params[param] = query_params[param].map(() => 1);
      }
    }

    yield put(actions.receive_sample_search_parameters({ query_params }));
  } catch (e) {
    console.error(e);
    yield put(actions.receive_sample_search_parameters_failed());
  }
}

export const sagas = {
  watchEveryRequestSampleSearchParameters: function* watchEveryRequestSampleSearchParameters() {
    yield takeLatest(actions.request_sample_search_parameters.toString(), onFetchSampleSearchParameters);
  },
};
